/**
 * i18n (W3.M5) — English, Bulgarian, French and German (D-026).
 *
 * Bulgarian is not a "later, maybe" locale for this product: the pilot firms
 * are Bulgarian, the seed data is Cyrillic, and the first national feed is the
 * BG Patent Office. Retrofitting i18n after the pages exist is the expensive
 * order, so the plumbing lands before the pages do.
 *
 * French and German were added the same day the language question was settled.
 * They are cheap *here* — two catalogues and two Intl tags — and expensive in
 * the matcher, which needs per-language phonetics. Do not read this file's size
 * as the cost of adding a language; see D-026.
 *
 * Three things live here and nowhere else:
 *   - the message catalogues,
 *   - `setLocale()`, the single writer of the persisted preference,
 *   - `activeLocale()`, a plain function so the Vue-free formatters in
 *     `utils/` can follow the locale without importing Vue.
 */
import { createI18n } from 'vue-i18n'

import en from './locales/en'
import bg from './locales/bg'
import fr from './locales/fr'
import de from './locales/de'

export const SUPPORTED = ['en', 'bg', 'fr', 'de'] as const
export type Locale = (typeof SUPPORTED)[number]

/** Each language names itself — a switcher that says "German" to a German
 * speaker is asking them to read the language they are trying to leave. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  bg: 'Български',
  fr: 'Français',
  de: 'Deutsch',
}

/** Where Intl gets its rules from — 'bg' alone gives the wrong date order. */
const INTL_LOCALES: Record<Locale, string> = {
  en: 'en-GB',
  bg: 'bg-BG',
  fr: 'fr-FR',
  de: 'de-DE',
}

const STORAGE_KEY = 'lx.locale'

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED as readonly string[]).includes(value)
}

/**
 * Stored choice → browser preference → English.
 *
 * `navigator.languages` is checked by prefix so that bg-BG, bg, and any other
 * regional variant all resolve.
 */
export function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en'
  const stored = window.localStorage?.getItem(STORAGE_KEY)
  if (isSupported(stored)) return stored
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = String(tag).split('-')[0]
    if (isSupported(base)) return base
  }
  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  /* The catalogues are hand-written, so a missing key is a bug worth seeing in
   * dev — but never a console flood in production. */
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
  messages: { en, bg, fr, de },
})

/**
 * The active locale, readable from non-Vue code.
 *
 * `utils/format.ts` and `utils/dates.ts` are deliberately Vue-free so they stay
 * unit-testable; they call this instead of holding a composable.
 */
export function activeLocale(): Locale {
  const current = i18n.global.locale.value
  return isSupported(current) ? current : 'en'
}

/** The BCP-47 tag to hand to Intl for the active locale. */
export function intlLocale(): string {
  return INTL_LOCALES[activeLocale()]
}

/**
 * Switch language: update Vue, persist the choice, and correct `<html lang>`.
 *
 * The `lang` attribute is not cosmetic — screen readers pick pronunciation
 * from it, and CSS `:lang()` plus hyphenation rules key off it.
 */
export function setLocale(locale: Locale): void {
  if (!isSupported(locale)) return
  i18n.global.locale.value = locale
  if (typeof window !== 'undefined') {
    window.localStorage?.setItem(STORAGE_KEY, locale)
    document.documentElement.setAttribute('lang', locale)
  }
}

/** Call once at start-up so `<html lang>` matches the detected locale. */
export function applyInitialLocale(): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', activeLocale())
  }
}

export default i18n
